/**
 * 62.61 (the owner: "on motorcycle as well you move fast enough that you get pop in / out … many are parallel and would
 * more efficiently be abstracted into some shared system"): one near-field streamer for what is made round the camera.
 * A client registers a cell grid on the planets it streams, a reach, and how to make and evict a cell; the streamer
 * keeps, per client and planet, the cells within the reach of the camera's ground point **made** (nearest first, those
 * in view before those behind, under one budget a frame that grows with the camera's height — far cells are cheap
 * impostors) and those past `dropFactor` × the reach for `dropDelayS` **evicted** (made again, the same, on return). The
 * ring is led along the camera's motion (`prefetchS` ahead: the motorcycle's heading, a commander's pan), so cells are
 * made before the camera reaches them. How a made cell appears (its fade-in) is the client's.
 *
 * **Presentation only** (decisions 112, 154: the camera decides only what is drawn). The streamer reads the camera and
 * the planets' positions and nothing else; its clients make and drop what they draw, never state.
 *
 * The forest's cells (E5.4) are its first client and the forest's scatter its second (rendering/groundScatter.ts: the
 * forest's grid, a nearer reach); the fine patch and the stands' own chunks move onto it one at a time (BUILDLIST 62.61;
 * ARCHITECTURE §5.2: anything made near the camera goes through it).
 */
import * as THREE from 'three';
import type { PlanetState } from '../types';
import { cellCentre, cellsNear } from '../core/cells';
import { lowestGround } from '../core/horizonCull';

/** The dials, one table (a client may override any of them for itself). */
export const NEAR_FIELD = {
  /** A made cell is dropped past this × the client's reach … */
  dropFactor: 1.25,
  /** … once it has been past it this long (seconds): no flicker on a camera that turns back. */
  dropDelayS: 2,
  /** The ring is led this far ahead along the camera's motion (seconds) … */
  prefetchS: 1.5,
  /** … but never further than this share of the reach. */
  maxLeadShare: 0.5,
  /** The making budget a frame (ms) near the ground … */
  budgetMs: 1.5,
  /** … rising to this … */
  budgetHighMs: 2,
  /** … by this height of the camera over the ground (people). */
  highPeople: 600,
  /**
   * The ledger (62.61 step 2): the near field's whole budget a frame (ms), claimed in priority order by what builds near
   * the camera — the fine patch's sliced rebuild, the stands' build queue, then the streamed cells — each up to its cap.
   */
  frameMs: 4,
  claims: { patch: 3, stands: 2 },
  /**
   * 62.61's follow-up (the owner's commander-view stutter): while the camera's ground point moves faster than this
   * (people a second — a commander's pan or glide; a person, a bike, never), at most `movingMakes` cells a frame — a cell
   * the camera only passes over is never made, so a pan churns less (each made cell's records are long-lived: their
   * promotion was the pan's GC pauses).
   * 69.76 (the owner: "it still blips in when you move fast"): only a camera above `movingAbovePeople` over the ground (the
   * squad's stop and up) — a ride low over the ground at that speed (a helicopter, a dropship) sees every cell it passes, and
   * makes them under the budget alone.
   */
  movingPeoplePerS: 60,
  movingMakes: 2,
  movingAbovePeople: 20,
};
export type NearFieldDials = typeof NEAR_FIELD;

/** What streams through the near field: a cell grid on some planets, a reach, and how to make and drop a cell. */
export interface NearFieldClient<H = unknown> {
  readonly name: string;
  /** The planets it streams on (indices into the state's planets). */
  planets(): readonly number[];
  /** Its grid on a planet: core/cells.ts cells per face edge. */
  cellsPerEdge(planetIndex: number): number;
  /**
   * Cells within this of the camera's ground point (world units, along the ground) are made. `view.far`: the farthest
   * ground this frame's view reaches on the planet (world units from the camera; Infinity when the horizon is in view) —
   * a client may hold no more than it can show.
   */
  reach(planetIndex: number, view?: { far: number }): number;
  /** Make a cell (null: nothing there — remembered as made). Draws only: never state. */
  make(planetIndex: number, planet: PlanetState, cell: number): H | null;
  /** Drop a made cell. */
  evict(planetIndex: number, cell: number, handle: H | null): void;
  /** Its own dials (the rest are the table's). */
  dials?: Partial<NearFieldDials>;
}

interface Slot { handle: unknown; outSince: number }
interface Track { cells: Map<number, Slot>; prevDir: THREE.Vector3 | null; lastAhead: THREE.Vector3 | null; lastDir: THREE.Vector3 | null; pending: number; still: number }
interface Entry { client: NearFieldClient; tracks: Map<number, Track> }

const _cam = new THREE.Vector3(), _pv = new THREE.Matrix4(), _frustum = new THREE.Frustum(), _sphere = new THREE.Sphere();

export interface NearFieldStats { made: number; evicted: number; pending: number; held: number; ms: number; budgetMs: number; /** the last frame's claims (ms) */ claimed: Record<string, number> }

/** The view's corners and edge midpoints (normalised device coordinates): its farthest ground lies on its rim or its horizon. */
const VIEW_RIM: ReadonlyArray<readonly [number, number]> = [[-1, -1], [1, -1], [-1, 1], [1, 1], [0, -1], [0, 1], [-1, 0], [1, 0]];
const _farCam = new THREE.Vector3(), _farRay = new THREE.Vector3();

export class NearFieldStreamer {
  private readonly clients: Entry[] = [];
  private made = 0;
  private evicted = 0;
  private pending = 0;
  private ms = 0;
  private lastBudget = 0;
  /** the frames' own clock (seconds, the sum of dt): the drop delay's measure */
  private clock = 0;
  /** the frame's ledger: open from beginFrame to the cells' making; what is left of frameMs */
  private ledgerOpen = false;
  private ledgerLeft = 0;
  private claimed: Record<string, number> = {};

  /**
   * `now`: the wall clock the budget is measured by (ms) — `performance.now` in the page; a test passes its own, so what a
   * frame makes is a count, not a race with the machine's load.
   */
  constructor(private readonly now: () => number = () => performance.now()) {}

  register(client: NearFieldClient): void { this.clients.push({ client, tracks: new Map() }); }

  /**
   * 62.61 step 2, the ledger: the host opens the frame's budget (NEAR_FIELD.frameMs) before anything builds near the
   * camera; each claimant takes `claim(cap)` — its cap or what is left — builds in its own order within it, and `spend`s
   * what it took; the streamed cells take the rest in `update`. What each draws is its own, unchanged: only when it is
   * built moves, under one cap a frame instead of one each.
   */
  beginFrame(): void { this.ledgerOpen = true; this.ledgerLeft = NEAR_FIELD.frameMs; this.claimed = {}; }
  /** A claimant's budget this frame (ms): its cap, or what is left of the frame's if less; with no frame open, its cap. */
  claim(cap: number): number { return this.ledgerOpen ? Math.max(0, Math.min(cap, this.ledgerLeft)) : cap; }
  /** The ledger's clock (ms): the one every claimant measures its build by — the page's wall clock, or a test's own. */
  time(): number { return this.now(); }
  /** What a claimant took (ms). */
  spend(name: string, ms: number): void {
    if (!this.ledgerOpen) return;
    this.ledgerLeft -= ms;
    this.claimed[name] = (this.claimed[name] ?? 0) + ms;
  }

  /**
   * One frame: for each client and planet, the cells to hold round the camera's (motion-led) ground point; the missing
   * ones made nearest first and in view first within the budget, the long-gone ones evicted. `person`: a person's height
   * (world units), the budget's height scale.
   */
  update(camera: THREE.Camera, dt: number, planets: readonly PlanetState[], person: number): void {
    const t0 = this.now();
    this.clock += dt;
    camera.updateMatrixWorld();
    camera.getWorldPosition(_cam);
    _pv.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    _frustum.setFromProjectionMatrix(_pv);
    const queue: Array<{ entry: Entry; pi: number; planet: PlanetState; cell: number; inView: boolean; near: number }> = [];
    let lowest = Infinity;
    let movingFast = false;
    for (const entry of this.clients) {
      const { client } = entry, dials = { ...NEAR_FIELD, ...client.dials };
      for (const pi of client.planets()) {
        const planet = planets[pi];
        if (!planet || planet.alive === false) continue;
        let track = entry.tracks.get(pi);
        if (!track) { track = { cells: new Map(), prevDir: null, lastAhead: null, lastDir: null, pending: 0, still: 0 }; entry.tracks.set(pi, track); }
        const v = new THREE.Vector3(_cam.x - planet.position.x, _cam.y - planet.position.y, _cam.z - planet.position.z);
        const dist = v.length();
        if (dist < 1e-9) continue;
        const d = v.clone().divideScalar(dist), R = planet.radius, reach = client.reach(pi, { far: this.viewFar(camera, planet, pi) });
        lowest = Math.min(lowest, dist - R);
        // the motion: the ground point's change since the last frame, led prefetchS ahead
        const lead = new THREE.Vector3();
        if (track.prevDir && dt > 0) lead.copy(d).sub(track.prevDir).multiplyScalar(dials.prefetchS / dt);
        // (the ground point's speed this frame, people a second: a fast one caps the frame's makes below)
        if (track.prevDir && dt > 0 && dist - R > NEAR_FIELD.movingAbovePeople * person && d.distanceTo(track.prevDir) * R / dt > NEAR_FIELD.movingPeoplePerS * person) movingFast = true;
        track.prevDir = d.clone();
        // (the lead at most half the reach: a fast pan leads a reach-sized ring, it does not swell one)
        const maxLead = (dials.maxLeadShare * reach) / R;
        if (lead.length() > maxLead) lead.setLength(maxLead);
        const ahead = d.clone().add(lead).normalize();
        const n = client.cellsPerEdge(pi);
        const halfCell = (Math.PI / 2) / n;   // a cell's half-width as an angle (face centre; generous at the corners)
        // the far side of the planet: nothing to hold when the camera is further off the ground than the reach
        if (dist - R > reach) {
          const now = this.clock;
          for (const [cell, slot] of track.cells) this.age(entry, pi, cell, slot, now, dials, track);
          continue;
        }
        // the ring is recomputed when the ground point (or its lead) has moved a quarter cell, or work is left over, or
        // an eviction may be due; otherwise this frame costs nothing here
        const moved = !track.lastAhead || !track.lastDir || track.lastAhead.dot(ahead) < Math.cos(halfCell / 2) || track.lastDir.dot(d) < Math.cos(halfCell / 2);
        track.still += dt;
        if (!moved && track.pending === 0 && track.still < dials.dropDelayS / 2) continue;
        track.still = 0;
        track.lastAhead = ahead.clone(); track.lastDir = d.clone();
        // the ring: round the camera's ground point and round its lead, each the reach
        const want = new Set(cellsNear(ahead, reach / R, n));
        for (const c of cellsNear(d, reach / R, n)) want.add(c);
        // evict: made cells past the drop reach of the camera's own point, long enough
        const dropAngle = (reach * dials.dropFactor) / R + halfCell;
        const cosDrop = Math.cos(dropAngle), now = this.clock;
        for (const [cell, slot] of track.cells) {
          const cc = cellCentre(cell, n);
          if (cc.x * d.x + cc.y * d.y + cc.z * d.z < cosDrop) this.age(entry, pi, cell, slot, now, dials, track);
          else slot.outSince = -1;
        }
        for (const cell of want) {
          if (track.cells.has(cell)) continue;
          const cc = cellCentre(cell, n);
          _sphere.center.set(planet.position.x + cc.x * R, planet.position.y + cc.y * R, planet.position.z + cc.z * R);
          _sphere.radius = 2 * halfCell * R;
          const dot = cc.x * ahead.x + cc.y * ahead.y + cc.z * ahead.z;
          queue.push({ entry, pi, planet, cell, inView: _frustum.intersectsSphere(_sphere), near: -dot });   // (−cos: nearer first)
        }
      }
    }
    // one queue, one budget: in view first, then nearest (to the led point); ties by client order, then cell
    queue.sort((a, b) => (a.inView === b.inView ? 0 : a.inView ? -1 : 1) || a.near - b.near || a.cell - b.cell);
    const k = Number.isFinite(lowest) ? Math.min(1, Math.max(0, lowest / (NEAR_FIELD.highPeople * person))) : 0;
    const budget = this.claim(NEAR_FIELD.budgetMs + (NEAR_FIELD.budgetHighMs - NEAR_FIELD.budgetMs) * k);   // (the cells take what the ledger has left)
    this.lastBudget = budget;
    // (the budget is the making's: finding what to make is the ring's own cost, kept small by recomputing it only on motion)
    const tm = this.now();
    let i = 0;
    for (; i < queue.length; i++) {
      if (i > 0 && (this.now() - tm > budget || (movingFast && i >= NEAR_FIELD.movingMakes))) break;
      const q = queue[i], track = q.entry.tracks.get(q.pi)!;
      track.cells.set(q.cell, { handle: q.entry.client.make(q.pi, q.planet, q.cell), outSince: -1 });
      this.made++;
    }
    this.spend('cells', this.now() - tm);
    this.ledgerOpen = false;   // the frame's ledger closes with the cells
    this.pending = queue.length - i;
    for (const e of this.clients) for (const t of e.tracks.values()) t.pending = 0;
    for (let j = i; j < queue.length; j++) queue[j].entry.tracks.get(queue[j].pi)!.pending++;
    this.ms += this.now() - t0;
  }

  /**
   * The farthest ground the view reaches on `planet` (world units from the camera), once a frame a planet: rays through
   * the view's corners and edge midpoints on the sphere of the planet's lowest ground (a valley can only lengthen it);
   * Infinity when one misses (the horizon in view: the ground's own reach then, as before).
   */
  private viewFar(camera: THREE.Camera, planet: PlanetState, pi: number): number {
    const kept = this.farMemo.get(pi);
    if (kept && kept.frame === this.clock && kept.planet === planet) return kept.far;
    const R = planet.heightmap && planet.basePositions ? lowestGround(planet) : planet.radius, cam = camera.getWorldPosition(_farCam);   // (a bare planet: its sphere)
    const ox = cam.x - planet.position.x, oy = cam.y - planet.position.y, oz = cam.z - planet.position.z;
    const c = ox * ox + oy * oy + oz * oz - R * R;
    let far = c <= 0 ? Infinity : 0;
    for (const [x, y] of VIEW_RIM) {
      if (far === Infinity) break;
      _farRay.set(x, y, 0.5).unproject(camera).sub(cam).normalize();
      const b = ox * _farRay.x + oy * _farRay.y + oz * _farRay.z, disc = b * b - c;
      if (disc < 0 || -b - Math.sqrt(disc) < 0) { far = Infinity; break; }
      far = Math.max(far, -b - Math.sqrt(disc));
    }
    this.farMemo.set(pi, { frame: this.clock, planet, far });
    return far;
  }
  private readonly farMemo = new Map<number, { frame: number; planet: PlanetState; far: number }>();

  /** A made cell past the drop reach: out since `now` (seconds), dropped once it has been out dropDelayS. */
  private age(entry: Entry, pi: number, cell: number, slot: Slot, now: number, dials: NearFieldDials, track: Track): void {
    if (slot.outSince < 0) { slot.outSince = now; return; }
    if (now - slot.outSince < dials.dropDelayS) return;
    entry.client.evict(pi, cell, slot.handle);
    track.cells.delete(cell);
    this.evicted++;
  }

  /** Cells made and evicted so far, still wanted, held now; time spent (ms) and the last frame's budget. */
  stats(): NearFieldStats {
    let held = 0;
    for (const e of this.clients) for (const t of e.tracks.values()) held += t.cells.size;
    return { made: this.made, evicted: this.evicted, pending: this.pending, held, ms: +this.ms.toFixed(1), budgetMs: +this.lastBudget.toFixed(2), claimed: { ...this.claimed } };
  }
}
